import type {ReactElement} from 'react';
import React, {useState} from 'react';
import type {VideoItem} from '@PosterWhiteboard/items/video-item/video-item.class';
import type {VideoSlideItem} from '@PosterWhiteboard/items/slideshow-item/slide-items/video-slide-item.class';
import {ContextualMenuMediaControlOptions} from '@Components/contextual-menu/components/contextual-menu-media-control-options';
import type {MediaControlsItemProps} from '@Components/contextual-menu/components/media-controls-item';
import {doesVideoHaveAudio} from '@Utils/video.util';
import {onClickRemoveVideoBG, onOpenReplaceMedia, onOpenTrimVideoModal} from '@Components/poster-editor/library/poster-editor-open-modals';
import {Icon} from '@Components/icon-v2';
import type {ItemData} from '@PosterWhiteboard/page/add-item.class';
import type {ReplaceMediaControlsButtons} from '@Components/replace-media-controls';
import {ContextualMenuReplaceMediaControls} from '@Components/contextual-menu/components/contextual-menu-replace-media-controls';
import {RemoveVideoBackgroundNotificationTag} from '@Components/poster-editor/components/remove-video-background-notification-tag';
import styles from './base-video-media-controls.module.scss';
import {getCreditsForVideoBGR, isBGRAllowedForPAYGUser} from '@Libraries/bg-removals-library';
import {FreeBgrPopup} from '@Components/free-bgr-popup';
import {isUserPremium} from '@Libraries/user.library';

interface BaseVideoMediaControlsProps {
  activeItem: VideoItem | VideoSlideItem;

  onReplaceVideo(itemData: ItemData): void;

  onUnMute(): void;
}

enum VideoMediaControlsOptions {
  TRIM = 'trim',
  REPLACE = 'replace',
  REMOVE_BG = 'remove-bg',
  UNMUTE = 'unmute',
}

export function BaseVideoMediaControls({...props}: BaseVideoMediaControlsProps): ReactElement {
  const [isReplaceOptionEnabled, setIsReplaceOptionEnabled] = useState(false);
  const isBGRAllowedForPAYG = isBGRAllowedForPAYGUser();
  const credits = getCreditsForVideoBGR();
  String(credits);

  const onTrimVideo = (): void => {
    onOpenTrimVideoModal(props.activeItem);
  };

  const onReplaceCallback = (id: ReplaceMediaControlsButtons): void => {
    onOpenReplaceMedia(id, props.activeItem, (itemData: ItemData): void => {
      props.onReplaceVideo(itemData);
    });
  };

  const onClickRemoveBG = async (): Promise<void> => {
    await onClickRemoveVideoBG(props.activeItem, credits);
  };

  const getUnMuteIcon = (): ReactElement => {
    return (
      <>
        <Icon icon="icon-sound" />
        <div
          className={`${props.activeItem.isMuted ? styles.muted : styles.unMuted} ${styles.unMuteIconLine} ${
            doesVideoHaveAudio(props.activeItem.htmlElement) ? '' : styles.disabled
          }`}
        />
      </>
    );
  };

  const onUnMute = (): void => {
    props.onUnMute();
  };

  const getCustomPopupForBGR = (): ReactElement | undefined => {
    if (!isUserPremium() && isBGRAllowedForPAYG && credits > 0) {
      if (props.activeItem.removeBackground.isBackgroundRemoved || props.activeItem.removeBackground.isBackgroundBeingRemoved) {
        return undefined;
      }

      return <FreeBgrPopup />;
    }

    return undefined;
  };

  const getItems = (): Array<MediaControlsItemProps> => {
    return [
      {
        id: `${VideoMediaControlsOptions.TRIM}-video-controls`,
        icon: 'icon-trim-video',
        text: window.i18next.t('pmwjs_trim'),
        onClick: onTrimVideo,
      },
      {
        id: `${VideoMediaControlsOptions.REPLACE}-video-controls`,
        icon: 'icon-replace',
        text: window.i18next.t('pmwjs_replace'),
        onClick: (): void => {
          setIsReplaceOptionEnabled(true);
        },
      },
      {
        id: `${VideoMediaControlsOptions.REMOVE_BG}-media-control`,
        icon: 'icon-remove-bg',
        text: window.i18next.t('pmwjs_ai_background_remover'),
        isLoading: props.activeItem.removeBackground.isBackgroundBeingRemoved,
        loadingText: window.i18next.t('pmwjs_removing_background'),
        tag: (
          <RemoveVideoBackgroundNotificationTag
            videoHashedFilename={props.activeItem.hashedFilename}
            startTime={props.activeItem.getStartTimeForNonRemovedBgVideo()}
            endTime={props.activeItem.getEndTimeForNonRemovedBgVideo()}
          />
        ),
        isSelected: props.activeItem.removeBackground.isBackgroundRemoved && !props.activeItem.removeBackground.isBackgroundBeingRemoved,
        selectedClasses: styles.aiBGLight,
        onClick: (): void => {
          void onClickRemoveBG();
        },
        textColorClasses: styles.aiText,
        textPaddingClasses: 'spacing-p-r-4 spacing-p-l-4',
        isTextBold: true,
        iconClasses: styles.aiText,
        tagContainerClassName: '_full-width',
        customPopup: getCustomPopupForBGR(),
      },
      {
        id: `${VideoMediaControlsOptions.UNMUTE}-video-controls`,
        customIconObject: getUnMuteIcon(),
        text: props.activeItem.isMuted ? window.i18next.t('pmwjs_unmute') : window.i18next.t('pmwjs_mute'),
        isDisabled: !doesVideoHaveAudio(props.activeItem.htmlElement),
        onClick: onUnMute,
      },
    ];
  };

  return isReplaceOptionEnabled ? (
    <ContextualMenuReplaceMediaControls
      id="background-image-replace-media-controls"
      controlsClassName={styles.replaceOption}
      onClick={onReplaceCallback}
      onCancel={(): void => {
        setIsReplaceOptionEnabled(false);
      }}
    />
  ) : (
    <ContextualMenuMediaControlOptions id="video-media-controls" items={getItems()} />
  );
}
